import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import HorizontalTimeline from "./HorizontalTimeline";
import { User, FileText, MapPin, ClipboardCheck, History, Plug2, Wand, Lightbulb, Building2, Copy } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import {
  EditOutlined,
  DownloadOutlined,
  ReconciliationOutlined,
  PaperClipOutlined,
  EllipsisOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import {
  Row,
  Col,
  message,
  Tabs,
  Skeleton,
  Alert,
  Divider,
  Typography,
  Space,
  Collapse,
  Button,
  Tooltip,
  Modal,
  Menu,
  Dropdown,
} from "antd";
import axios from "axios";
import VisitPreview from "./VisitPreview";
import Documents from "./Documents";
import Notifications from "./Notifications";
import getTemplateFields from "./templateFields";
import StatusSelector from "./verification_preview/StatusSelector";
import SectionViews from "./verification_preview/SectionViews";
import TemplatesSection from "./verification_preview/TemplatesSection";
import NormasForm from "./verification_preview/NormasForm";
import { GeneralForm } from "./forms";
import VisitsWrapper from "./VisitsWrapper";
import moment from "moment";
import ViewConfigurationModal from "./verification_preview/ViewConfigurationModal";
import "./VerificationPreview.css";

import { useAuth } from "../../layout/auth";

export default function VerificationPreview() {
  const { id } = useParams();
  const [verification, setVerification] = useState();
  const [visits, setVisits] = useState();
  const [selectedStatus, setSelectedStatus] = useState();
  const [notifications, setNotifications] = useState([]);
  const [user, setUser] = useState();
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState(false);
  const [timelineVisible, setTimelineVisible] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isGeneralFormVisible, setIsGeneralFormVisible] = useState(false);
  const [isViewConfigModalVisible, setIsViewConfigModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState();

  const auth = useAuth();

  const menu = (
    <Menu>
      <Menu.Item key="3" onClick={() => setIsGeneralFormVisible(true)} disabled={verification?.Status > 1 && auth.Rol !== "director"}>
        Modificar Folio
      </Menu.Item>
      <Menu.Item key="2" onClick={() => setIsViewConfigModalVisible(true)}>
        Configurar esta vista
      </Menu.Item>
      <Menu.Item
        key="1"
        onClick={() => {
          setLoading(true);
          axios
            .get(`${process.env.REACT_APP_API_URL}/verificaciones/${id}`, {
              params: { u: auth.UID },
            })
            .then((res) => {
              setVerification(res.data[0]);
              setLoading(false);
            })
            .catch((err) => {
              setLoading(false);
              message.error(err.message);
            });
        }}
      >
        Actualizar vista
      </Menu.Item>
    </Menu>
  );

  const templateFields = getTemplateFields(verification, visits);
  const iconCircleStyle = { fontSize: "20px", borderRadius: "50%", border: "1px solid #1890ff", padding: "5px", backgroundColor: "#fff" };

  const documentCategories = {
    generalDocs: [
      // "Portada",
      "Documento F06: Contrato y Solicitud",
      "Informe de pruebas",
      "Formato de supervisión",
      "Listas de inspección en sitio",
      "Listas de inspección de proyecto",
      // "Vista Previa NOM 001",
      // "Dictamen NOM 007",
      // "Dictamen NOM 013",
      "Transformador",
      "Evidencia de inspección en sitio",
      "Evidencia fotográfica",
      "Revisión de proyecto",
    ],
    under100Docs: ["Diagrama unifilar", "Cuadro de relación de Cargas"],
    over100Docs: [
      "Diagrama unifilar",
      "Cuadro de relación de Cargas",
      "Memoria técnica",
      "Planos de planta y elevación",
      "Carta responsiva",
    ],
    before2012Docs: [
      "Anexo B",
      "Documento que acredite que la Instalación fue construida antes de la entrada en vigor de la NOM-001-SEDE-2012",
    ],
    dangerousDocs: ["Anexo B", "Dictamen de Verificación"],
    visitsDocs: ["Acta de evaluación de la conformidad"],
    nomDocs: ["Planos eléctricos", "Memoria de cálculo de DPEA", "Plano general del sistema"],
  };

  // Fetch verification
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${process.env.REACT_APP_API_URL}/verificaciones/${id}`, {
        params: { u: auth.UID },
      })
      .then((res) => {
        setVerification(res.data[0]);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        message.error(err.message);
      });
  }, [id]);

  // Fetch notifications
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/notificaciones/noLeidas/porVerificacion/${id}`, {
        params: { u: auth.UID },
      })
      .then((res) => {
        setNotifications(res.data);

        // If there are unread notifications, mark them as read
        if (res.data.length > 0) {
          axios
            .put(`${process.env.REACT_APP_API_URL}/notificaciones/marcarComoLeidas/porVerificacion/${id}`, {
              u: auth.UID,
            })
            .then(() => {
              console.log("Notifications marked as read");
            })
            .catch((err) => console.error("Error marking notifications as read:", err));
        }
      })
      .catch((err) => console.error("Error fetching notifications:", err));
  }, [id]);

  // Set selected status
  useEffect(() => {
    if (verification) {
      setSelectedStatus(verification?.Status);
    }
  }, [verification]);

  // Fetch visits
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/visitas/${id}`)
      .then((res) => {
        setVisits(res.data);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [id]);

  // Fetch user
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/usuarios/getUsername/${verification?.UsuarioID}`)
      .then((res) => {
        setUser(res.data[0]);
      })
      .catch((err) => {
        message.error(err.message);
      });
  }, [verification]);

  const handleViewConfigFinish = (values) => {
    setHidden(values.hidden);
    setTimelineVisible(values.timelineVisible);
    setIsViewConfigModalVisible(false);
  };

  const copyToClipboard = (text, successMessage = "Copiado al portapapeles") => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          message.success(successMessage);
        })
        .catch((err) => {
          console.error("Error al copiar al portapapeles con navigator.clipboard:", err);
          // Fallback to execCommand
          fallbackCopyToClipboard(text, successMessage);
        });
    } else {
      // Fallback for browsers that don't support navigator.clipboard
      fallbackCopyToClipboard(text, successMessage);
    }
  };

  const fallbackCopyToClipboard = (text, successMessage) => {
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.style.position = "fixed"; // Avoid scrolling to bottom
    textarea.style.left = "-9999px"; // Move off-screen
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand("copy");
      message.success(successMessage);
    } catch (err) {
      console.error("Error al copiar al portapapeles con execCommand:", err);
      message.error("Error al copiar al portapapeles");
    } finally {
      document.body.removeChild(textarea);
    }
  };

  useEffect(() => {
    if (verification && !activeTab) {
      if (verification.Nom001 === "Sí" || verification.Nom007 === "Sí" || verification.Nom013 === "Sí") {
        setActiveTab("nom001");
      } else {
        setActiveTab("3");
      }
    }
  }, [verification, activeTab]);

  const applicableNorms = [];
  if (verification?.Nom001 === "Sí") applicableNorms.push("NOM-001");
  if (verification?.Nom007 === "Sí") applicableNorms.push("NOM-007");
  if (verification?.Nom013 === "Sí") applicableNorms.push("NOM-013");

  return (
    <>
      {/* -------------------------- NOTIFICATIONS -------------------------- */}
      {notifications.length > 0 && (
        <Row>
          <Col span={24}>
            {notifications.map((notification) => (
              <Alert
                key={notification.NotificacionID}
                message={notification.Mensaje}
                type="warning"
                showIcon
                closable
                style={{ marginBottom: "5px" }}
              />
            ))}
          </Col>
        </Row>
      )}

      {/* -------------------------- HEADER -------------------------- */}
      <Row>
        <Col span={24} style={{ textAlign: "right", marginBottom: "20px" }}></Col>
      </Row>

      <Row gutter={[24, 24]} align="middle">
        {/* Folio + Status (Always Visible) */}
        <Col xs={24} sm={6}>
          <Space>
            <Space direction="vertical" size="small">
              {loading ? (
                <Skeleton.Input active />
              ) : (
                <Space>
                  <Typography.Title level={4} style={{ margin: 0 }}>
                    <Typography.Text type="secondary">Verificación: </Typography.Text>
                    {verification?.ControlInterno}
                  </Typography.Title>
                  <Dropdown overlay={menu} trigger={["click"]}>
                    <Button type="default" size="small" icon={<EllipsisOutlined rotate={90} />} />
                  </Dropdown>
                </Space>
              )}
              {loading ? (
                <Skeleton.Input active />
              ) : (
                <StatusSelector
                  id={id}
                  verification={verification}
                  setVerification={setVerification}
                  selectedStatus={selectedStatus}
                  setSelectedStatus={setSelectedStatus}
                />
              )}
            </Space>
          </Space>
        </Col>

        {/* The following data is only visible on larger screens */}
        <Col xs={0} sm={18}>
          <Row gutter={[24, 24]} align="middle">
            {/* User Info (Avatar + Username) */}
            {auth.Rol === "director" && (
              <Col sm={6} md={6}>
                <Space>
                  <User size={24} />
                  <Space direction="vertical" size={2}>
                    <Typography.Title level={5} style={{ margin: 0, whiteSpace: "nowrap" }}>
                      {user?.NombreCompleto && user.NombreCompleto.length > 20
                        ? `${user.NombreCompleto.substring(0, 20)}...`
                        : user?.NombreCompleto}
                    </Typography.Title>
                    <Typography.Text ellipsis style={{ margin: 0 }}>
                      {user?.Rol}
                    </Typography.Text>
                  </Space>
                </Space>
              </Col>
            )}

            {/* Normas Aplicables */}
            <Col xs={0} sm={auth.Rol === "director" ? 6 : 12} md={auth.Rol === "director" ? 6 : 12}>
              <Space>
                <FileTextOutlined style={{ fontSize: 24 }} />
                <Space direction="vertical" size={2}>
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    <Space>
                      Normas aplicables
                      <Button
                        type="default"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => setIsModalVisible(true)}
                        className={applicableNorms.length === 0 ? "pulsating-border" : ""}
                      />
                    </Space>
                  </Typography.Title>
                  <Space split={<Divider type="vertical" />}>
                    {(() => {
                      if (applicableNorms.length > 0) {
                        return applicableNorms.map((norm, index) => <Typography.Text key={index}>{norm}</Typography.Text>);
                      } else {
                        return <Typography.Text>No se han seleccionado</Typography.Text>;
                      }
                    })()}
                  </Space>
                </Space>
              </Space>
            </Col>

            {/* PV Info */}
            <Col xs={0} sm={auth.Rol === "director" ? 8 : 12} md={auth.Rol === "director" ? 6 : 12}>
              <Space>
                <Building2 size={24} />
                <Space direction="vertical" size={2} style={{ flex: 1, minWidth: 0 }}>
                  {/* Name */}
                  <Typography.Title level={5} style={{ margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    <Space>
                      {verification?.NombreCompletoPV && verification.NombreCompletoPV.length > 20
                        ? `${verification.NombreCompletoPV.substring(0, 20)}...`
                        : verification?.NombreCompletoPV}
                      <Tooltip title="Copiar nombre">
                        <Button
                          type="text"
                          size="small"
                          icon={<Copy size={14} />}
                          onClick={() => copyToClipboard(verification?.NombreCompletoPV)}
                        />
                      </Tooltip>
                    </Space>
                  </Typography.Title>

                  {/* Address */}
                  <div style={{ display: "flex", alignItems: "center", overflow: "hidden" }}>
                    <Typography.Text
                      style={{
                        margin: 0,
                        flexShrink: 1,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "30ch", // approx 30 characters
                      }}
                    >
                      {`${verification?.NombreCalle || ""}` +
                        `${verification?.NumeroOficial ? ` ${verification.NumeroOficial}` : " S/N"}` +
                        `${verification?.ColoniaOAsentamiento ? `, Col. ${verification.ColoniaOAsentamiento}` : ""}` +
                        `${verification?.Ciudad ? `, ${verification.Ciudad}` : ""}` +
                        `${verification?.Estado ? `, ${verification.Estado}` : ""}`}
                    </Typography.Text>
                    <Tooltip title="Copiar dirección">
                      <Button
                        type="text"
                        size="small"
                        icon={<Copy size={14} />}
                        onClick={() =>
                          copyToClipboard(
                            `${verification?.NombreCalle || ""}` +
                              `${verification?.NumeroOficial ? ` ${verification.NumeroOficial}` : " S/N"}` +
                              `${verification?.ColoniaOAsentamiento ? `, Col. ${verification.ColoniaOAsentamiento}` : ""}` +
                              `${verification?.Ciudad ? `, ${verification.Ciudad}` : ""}` +
                              `${verification?.Estado ? `, ${verification.Estado}` : ""}`
                          )
                        }
                      />
                    </Tooltip>
                  </div>
                </Space>
              </Space>
            </Col>
          </Row>
        </Col>
      </Row>

      <Modal
        title="Editar Normas Aplicables"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null} // Hide default footer buttons
      >
        <NormasForm
          initialValues={{
            Nom001: verification?.Nom001 === "Sí",
            Nom007: verification?.Nom007 === "Sí",
            Nom013: verification?.Nom013 === "Sí",
          }}
          onFinish={(values) => {
            const updatedVerification = {
              ...verification,
              Nom001: values.Nom001 ? "Sí" : "No",
              Nom007: values.Nom007 ? "Sí" : "No",
              Nom013: values.Nom013 ? "Sí" : "No",
            };
            axios
              .put(`${process.env.REACT_APP_API_URL}/verificaciones/${id}`, updatedVerification)
              .then(() => {
                message.success("Normas actualizadas correctamente");
                console.log("Verification before update:", verification);
                setVerification(updatedVerification);
                console.log("Verification after update (should be updated):", updatedVerification);
                setIsModalVisible(false);
              })
              .catch((err) => {
                message.error("Error al actualizar las normas");
                console.error(err);
              });
          }}
          onCancel={() => setIsModalVisible(false)}
        />
      </Modal>

      <ViewConfigurationModal
        visible={isViewConfigModalVisible}
        onCancel={() => setIsViewConfigModalVisible(false)}
        onFinish={handleViewConfigFinish}
        initialValues={{ hidden, timelineVisible }}
      />

      <Row>
        {/* For large screens */}
        <Col xs={0} lg={24}>
          <AnimatePresence>
            {timelineVisible && (
              <motion.div initial={{ opacity: 0, height: 0, y: -20 }} animate={{ opacity: 1, height: "auto", y: 0 }}>
                <HorizontalTimeline verification={verification} visits={visits} />
              </motion.div>
            )}
          </AnimatePresence>
          {!timelineVisible && <Divider />}
        </Col>

        {/* For small screens — always show divider */}
        <Col xs={24} lg={0}>
          <Divider />
        </Col>
      </Row>

      {/* -------------------------- VERIFICATION BODY -------------------------- */}
      <Tabs
        type="line"
        size="middle"
        animated={true}
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        className="custom-tabs"
        tabBarGutter={16}
      >
        <Tabs.TabPane
          tab={
            <div style={{ display: "flex", flexDirection: "Column", width: "90px", alignItems: "center" }}>
              <Plug2 style={{ fontSize: "20px" }} />
              <span>Verificación</span>
            </div>
          }
          key="nom001"
        >
          <Collapse bordered={false} expandIconPosition="right" defaultActiveKey={["1", "2"]}>
            <Collapse.Panel
              header={
                <div>
                  <DownloadOutlined style={iconCircleStyle} />
                  <Typography.Text style={{ marginLeft: "10px" }}>Descarga de formatos</Typography.Text>
                </div>
              }
              key="1"
            >
              <TemplatesSection templateFields={templateFields} loading={loading} id={id} />
            </Collapse.Panel>
            <Collapse.Panel
              header={
                <div>
                  <ReconciliationOutlined style={iconCircleStyle} />
                  <Typography.Text style={{ marginLeft: "10px" }}>Información de verificación</Typography.Text>
                </div>
              }
              key="2"
            >
              <SectionViews
                id={id}
                verification={verification}
                setVerification={setVerification}
                hidden={hidden}
                loading={loading}
                templateFields={templateFields}
              />
            </Collapse.Panel>
          </Collapse>
        </Tabs.TabPane>
        {/* REVISION TAB */}
        {(verification?.Nom001 === "Sí" || verification?.Nom007 === "Sí" || verification?.Nom013 === "Sí") && (
          <Tabs.TabPane
            tab={
              <Tooltip
                title={
                  !verification?.TipoVerificacionID || !verification?.AlcanceVerificacion
                    ? "Completa Tipo de Verificación y Alcance para habilitar esta sección."
                    : null
                }
              >
                <div style={{ display: "flex", flexDirection: "column", width: "90px", alignItems: "center" }}>
                  <FileText style={{ fontSize: "20px" }} />
                  <span>Revisión</span>
                </div>
              </Tooltip>
            }
            key="4"
            disabled={!verification?.TipoVerificacionID || !verification?.AlcanceVerificacion}
          >
            <Documents
              id={id}
              verification={verification}
              title="Documentos por tipo de verificación"
              list={
                (() => {
                  let docs = [];
                  const isN07 = verification?.Nom007 === "Sí";
                  const isN13 = verification?.Nom013 === "Sí";
                  const isN01 = verification?.Nom001 === "Sí";

                  if (isN01) {
                    if (verification?.TipoVerificacionID) {
                      if (
                        verification?.TipoVerificacionID === 1 ||
                        verification?.TipoVerificacionID === 2 ||
                        verification?.TipoVerificacionID === 3
                      ) {
                        docs =
                          verification?.AlcanceVerificacion >= 100
                            ? [...documentCategories.over100Docs]
                            : [...documentCategories.under100Docs];
                      } else if (verification?.TipoVerificacionID === 4) {
                        docs = [...documentCategories.dangerousDocs];
                      } else if (verification?.TipoVerificacionID === 5) {
                        docs = [...documentCategories.before2012Docs];
                      }
                    }
                  }
                  
                  if (isN07 || isN13) {
                    docs = [...docs, ...documentCategories.nomDocs];
                  }

                  return [...new Set(docs)];
                })()
              }
              user={user}
              usage="revision"
              setVerification={setVerification}
            />
          </Tabs.TabPane>
        )}

        {/* VISITA TAB */}
        {(verification?.Nom001 === "Sí" || verification?.Nom007 === "Sí" || verification?.Nom013 === "Sí") && (
          <Tabs.TabPane
            tab={
              <div style={{ display: "flex", flexDirection: "Column", width: "90px", alignItems: "center" }}>
                <MapPin style={{ fontSize: "20px" }} />
                <span>Visitas</span>
              </div>
            }
            key="2"
          >
            <VisitsWrapper id={id} verification={verification} visits={visits} setVisits={setVisits} />
            <Divider />
            <Collapse bordered={false} expandIconPosition="right">
              <Collapse.Panel
                header={
                  <div>
                    <PaperClipOutlined
                      style={{
                        fontSize: "20px",
                        borderRadius: "50%",
                        border: "1px solid #1890ff",
                        padding: "5px",
                        backgroundColor: "#fff",
                      }}
                    />
                    <Typography.Text style={{ marginLeft: "10px" }}>Documentos de las visitas</Typography.Text>
                  </div>
                }
                key="1"
              >
                <Documents
                  id={id}
                  verification={verification}
                  title="Documentos de la visita"
                  list={documentCategories.visitsDocs}
                  setVisits={setVisits}
                  user={user}
                  usage="visits"
                  setVerification={setVerification}
                />
              </Collapse.Panel>
            </Collapse>
          </Tabs.TabPane>
        )}

        <Tabs.TabPane
          tab={
            <div style={{ display: "flex", flexDirection: "Column", width: "90px", alignItems: "center" }}>
              <ClipboardCheck style={{ fontSize: "20px" }} />
              <span>Documentos</span>
            </div>
          }
          key="3"
        >
          <Documents
            id={id}
            verification={verification}
            title="Otros documentos del expediente"
            list={documentCategories.generalDocs}
            user={user}
            usage="paperwork"
            setVerification={setVerification}
          />
        </Tabs.TabPane>

        {/* NOTIFICACIONES TAB */}
        <Tabs.TabPane
          tab={
            <div style={{ display: "flex", flexDirection: "Column", width: "90px", alignItems: "center" }}>
              <History style={{ fontSize: "20px" }} />
              <span>Actividad</span>
            </div>
          }
          key="5"
        >
          <Notifications id={id} verification={verification} notifications={notifications} />
        </Tabs.TabPane>
      </Tabs>
      {verification && (
        <GeneralForm
          mainData={verification}
          setMainData={setVerification}
          setVisible={setIsGeneralFormVisible}
          visible={isGeneralFormVisible}
          title="Información general"
        />
      )}
    </>
  );
}
